// PricingPage.js — public pricing + interactive ROI calculator.
// No auth required. Marketed at prospects landing from LinkedIn / podcasts /
// yabbit.com.au. The ROI calculator anchors the CFO conversation before
// they even talk to sales — reps × avg deal size × recovery % = revenue
// they're leaving on the table today.
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Sparkles, ShieldCheck, TrendingUp, Zap } from 'lucide-react';

const TIERS = [
  {
    id: 'grade', name: 'Grade', price: 49, minMonthly: 299,
    tagline: 'One role · testing the water',
    features: [
      '100% grading against your KB',
      '**60 processed minutes per user included**',
      '1 role KB (choose sales, support, admin, accounts or management)',
      'Rep-facing training modules + score dashboards',
      'CSV export',
    ],
    highlight: false,
  },
  {
    id: 'coach', name: 'Coach', price: 89, minMonthly: 499,
    tagline: 'All 5 roles · where most teams land',
    features: [
      'Everything in Grade',
      '**150 processed minutes per user included**',
      'All 5 role KBs (sales, support, admin, accounts, management)',
      'AI Suggest Focus + evidence calls',
      'Coaching Records + reassessment tracking',
      'Governance streak detection + sentiment',
      'Filler-word + interruption detection',
      'Custom report builder',
    ],
    highlight: true,
  },
  {
    id: 'intelligence', name: 'Intelligence', price: 149, minMonthly: 999,
    tagline: 'Multi-site · revenue-focused',
    features: [
      'Everything in Coach',
      '**300 processed minutes per user included**',
      'Revivable Deal Queue + attribution',
      'Regional Rollups (multi-site)',
      'VoiceFabric flow canvas (real-time automations)',
      'Nightly calibration + drift detection',
      'API access · priority support',
    ],
    highlight: false,
  },
  {
    id: 'enterprise', name: 'Enterprise', price: null, priceLabel: 'Custom',
    tagline: '200+ seats · Reseller · White-label',
    features: [
      'Everything in Intelligence',
      'Custom minute allowance',
      'SSO / SAML · dedicated CSM',
      'Custom KB engineering',
      'White-label / reseller portal',
      '99.9% SLA · data residency options',
    ],
    highlight: false,
  },
];

const OVERAGE_COPY = 'Beyond your included minutes: $0.07/min transcription · $0.09/min sentiment. Predictable headline price, no surprises.';

// Live-market pricing snapshot (Aug 2026) — sourced from vendor sites.
const COMPETITORS = [
  { name: 'Aircall AI Assist Pro', vertical: 'Add-on to Aircall telephony', price: 'A$76/user/mo + phone plan', setup: 'Quote', notes: 'Requires Aircall phone platform · replaces your UCaaS' },
  { name: 'Dialpad Sell', vertical: 'US · sales + telephony bundle', price: 'A$60 – A$232/user/mo', setup: 'Custom', notes: 'Includes phone platform · replaces your UCaaS' },
  { name: 'Dialpad Support', vertical: 'US · support contact centre', price: 'A$124 – A$232/user/mo', setup: 'Custom', notes: 'Replaces UCaaS · support-focused' },
  { name: 'RingCX (AU)', vertical: 'AU · full contact centre', price: 'A$115/user/mo+', setup: 'Enterprise', notes: 'Contact-centre platform · replaces UCaaS' },
  { name: 'Avoma', vertical: 'US · sales & CS conversation intelligence', price: 'A$74 – A$105/user/mo effective', setup: 'Self-serve', notes: 'Sales/CS only · not reception, accounts or admin' },
  { name: 'Gong', vertical: 'US enterprise · sales-only', price: 'A$220-300/user/mo', setup: 'A$12K-100K', notes: 'Sales only · platform fee · 50-seat sweet spot' },
  { name: 'Salesforce Conversation Insights', vertical: 'Salesforce sales teams', price: 'A$70/user/mo', setup: 'Salesforce project', notes: 'Sales only · Salesforce-native' },
  { name: 'Observe.AI / Level AI / CallMiner', vertical: 'Enterprise contact centre', price: 'Quote', setup: 'A$30K+', notes: '100+ seat contact centres · not SMB' },
];

const RoiCalculator = () => {
  const [reps, setReps] = useState(15);
  const [avgDeal, setAvgDeal] = useState(3500);
  const [dealsPerRep, setDealsPerRep] = useState(4);
  const [recoveryPct, setRecoveryPct] = useState(15);
  const [scoreLift, setScoreLift] = useState(20);
  const [managerCost, setManagerCost] = useState(85000);
  const [tier, setTier] = useState(89);

  const numbers = useMemo(() => {
    const monthlyRevenue = reps * dealsPerRep * avgDeal;
    const annualRevenue = monthlyRevenue * 12;
    // Deal recovery — Revivable Queue surfaces ~10-20% of lost deals in
    // customer data. We apply the % against LOST deals only, which we
    // proxy at 60% of closed volume (typical close rate 40%).
    const lostDealsAnnual = annualRevenue * 1.5; // reps close ~40% → lost ≈ 1.5× wins
    const recoveredAnnual = lostDealsAnnual * (recoveryPct / 100) * 0.35; // 35% of surfaced revive → convert
    // Score lift → close-rate lift. Every 10pts of score ≈ +1.5% close-rate.
    // Applied to CURRENT pipeline, not fantasy revenue.
    const conversionLiftAnnual = annualRevenue * (scoreLift / 10) * 0.015;
    // Manager time saved — 65% of ~10hrs/wk on QA, 1 manager per 15 reps.
    const managerHourly = managerCost / 1920;
    const managerCount = Math.max(1, Math.ceil(reps / 15));
    const managerSavingsAnnual = 10 * 0.65 * managerHourly * 48 * managerCount;
    const vfCostAnnual = reps * tier * 12;
    const totalBenefit = recoveredAnnual + conversionLiftAnnual + managerSavingsAnnual;
    const netAnnual = totalBenefit - vfCostAnnual;
    const roiMultiple = vfCostAnnual > 0 ? netAnnual / vfCostAnnual : 0;
    const paybackMonths = totalBenefit > 0
      ? Math.max(0.5, (vfCostAnnual / totalBenefit) * 12)
      : 0;
    return {
      recoveredAnnual: Math.round(recoveredAnnual),
      conversionLiftAnnual: Math.round(conversionLiftAnnual),
      managerSavingsAnnual: Math.round(managerSavingsAnnual),
      vfCostAnnual: Math.round(vfCostAnnual),
      netAnnual: Math.round(netAnnual),
      roiMultiple: roiMultiple.toFixed(1),
      paybackMonths: paybackMonths.toFixed(1),
    };
  }, [reps, avgDeal, dealsPerRep, recoveryPct, scoreLift, managerCost, tier]);

  const fmt = (n) => `$${n.toLocaleString('en-AU')}`;

  return (
    <div className="bg-gradient-to-br from-slate-900 via-slate-950 to-teal-950 rounded-3xl p-8 border border-teal-500/20" data-testid="roi-calculator" data-force-dark="true">
      <div className="flex items-center gap-3 mb-6">
        <TrendingUp className="w-6 h-6 text-teal-400" />
        <h2 className="text-2xl font-bold text-white">ROI Calculator</h2>
      </div>
      <p className="text-white/80 text-sm mb-8">
        Estimate what VoiceFabric would be worth to your team. Every number is editable — start with our defaults and dial them to your reality.
      </p>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Inputs */}
        <div className="space-y-5">
          {[
            { label: 'Phone-facing reps', v: reps, s: setReps, min: 1, max: 500, step: 1, suffix: 'reps' },
            { label: 'Avg deal size', v: avgDeal, s: setAvgDeal, min: 100, max: 100000, step: 100, prefix: '$' },
            { label: 'Deals closed per rep per month', v: dealsPerRep, s: setDealsPerRep, min: 1, max: 100, step: 1, suffix: '/mo' },
            { label: 'Expected lost-deal recovery %', v: recoveryPct, s: setRecoveryPct, min: 0, max: 30, step: 1, suffix: '%' },
            { label: 'Expected score lift (points)', v: scoreLift, s: setScoreLift, min: 0, max: 40, step: 1, suffix: 'pts' },
            { label: 'Manager fully-loaded cost/year', v: managerCost, s: setManagerCost, min: 40000, max: 250000, step: 5000, prefix: '$' },
          ].map((f) => (
            <label key={f.label} className="block" data-testid={`roi-input-${f.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-white font-medium text-sm">{f.label}</span>
                <span className="text-teal-300 font-mono text-sm font-semibold">
                  {f.prefix || ''}{f.v.toLocaleString('en-AU')}{f.suffix || ''}
                </span>
              </div>
              <input
                type="range"
                min={f.min}
                max={f.max}
                step={f.step}
                value={f.v}
                onChange={(e) => f.s(Number(e.target.value))}
                className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer accent-teal-400"
              />
            </label>
          ))}
          <div>
            <div className="text-white font-medium text-sm mb-1.5">VoiceFabric tier</div>
            <div className="flex gap-2">
              {[
                { p: 49, name: 'Grade' },
                { p: 89, name: 'Coach' },
                { p: 149, name: 'Intelligence' },
              ].map((o) => (
                <button
                  key={o.p}
                  onClick={() => setTier(o.p)}
                  className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-colors ${
                    tier === o.p
                      ? 'bg-teal-500 text-white'
                      : 'bg-white/10 text-white/80 hover:bg-white/20 hover:text-white'
                  }`}
                  data-testid={`roi-tier-${o.name.toLowerCase()}`}
                >
                  {o.name} · ${o.p}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Outputs */}
        <div className="bg-black/40 rounded-2xl p-6 border border-white/10">
          <p className="text-teal-300 text-xs font-bold uppercase tracking-wider mb-4">Annual Impact</p>
          <div className="space-y-3 mb-6">
            <Row label="Deal recovery (Revivable Queue)" value={fmt(numbers.recoveredAnnual)} />
            <Row label="Conversion lift (from coaching)" value={fmt(numbers.conversionLiftAnnual)} />
            <Row label="Manager time saved" value={fmt(numbers.managerSavingsAnnual)} />
            <Row label="VoiceFabric cost" value={`− ${fmt(numbers.vfCostAnnual)}`} negative />
          </div>
          <div className="border-t border-white/15 pt-4">
            <Row label="Net annual impact" value={fmt(numbers.netAnnual)} big />
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="bg-teal-500/15 border border-teal-500/40 rounded-lg p-3 text-center">
                <p className="text-teal-300 text-2xl font-black" data-testid="roi-multiple">{numbers.roiMultiple}×</p>
                <p className="text-white/80 text-[10px] uppercase tracking-wider mt-0.5 font-semibold">ROI</p>
              </div>
              <div className="bg-amber-500/15 border border-amber-500/40 rounded-lg p-3 text-center">
                <p className="text-amber-300 text-2xl font-black" data-testid="roi-payback">{numbers.paybackMonths}<span className="text-sm ml-1">mo</span></p>
                <p className="text-white/80 text-[10px] uppercase tracking-wider mt-0.5 font-semibold">Payback</p>
              </div>
            </div>
          </div>
          <p className="text-white/50 text-[10px] mt-4 italic leading-relaxed">
            Estimates based on average VoiceFabric customer outcomes. Actual results vary
            with call volume, KB quality, and coaching consistency. Not a guarantee.
          </p>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, negative, big }) => (
  <div className="flex items-center justify-between">
    <span className={`${big ? 'text-white font-semibold' : 'text-white/85'} text-sm`}>{label}</span>
    <span
      className={`font-mono tabular-nums font-semibold ${
        big ? 'text-white text-2xl font-bold' :
        negative ? 'text-rose-300' : 'text-emerald-300'
      }`}
    >
      {value}
    </span>
  </div>
);

const PricingPage = () => (
  <div className="min-h-screen bg-[#0A0F1C]" data-testid="pricing-page">
    {/* Header */}
    <header className="border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-teal-500 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <span className="text-white font-bold">Yabbit VoiceFabric</span>
        </Link>
        <Link
          to="/login"
          className="text-sm text-teal-300 hover:text-teal-200 font-semibold"
          data-testid="pricing-signin-link"
        >
          Sign in →
        </Link>
      </div>
    </header>

    {/* Hero */}
    <section className="max-w-4xl mx-auto px-6 pt-20 pb-12 text-center">
      <h1 className="text-4xl sm:text-5xl font-black text-white leading-tight tracking-tight">
        One <span className="text-teal-400">Australian platform</span> that turns calls across sales, support, management, administration and accounts into role-specific grading, evidence-based coaching, governance and measurable behaviour change<span className="text-white/60"> — without replacing your Yabbit phone system.</span>
      </h1>
      <p className="text-white/70 text-lg mt-6 max-w-2xl mx-auto">
        Priced per Australian seat, with <strong className="text-white">included minutes on every plan</strong> and simple overage — not a US$150/user enterprise contract.
      </p>
      <p className="text-teal-300 text-sm mt-4">
        14-day free trial · No credit card · Onboarding call included
      </p>
      <p className="text-white/40 text-xs mt-2 max-w-xl mx-auto">
        {OVERAGE_COPY}
      </p>
    </section>

    {/* Tiers */}
    <section className="max-w-7xl mx-auto px-6 py-12">
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {TIERS.map((t) => (
          <div
            key={t.id}
            className={`relative rounded-2xl p-6 border ${
              t.highlight
                ? 'bg-gradient-to-b from-teal-500/10 to-transparent border-teal-500/40 ring-2 ring-teal-500/30'
                : 'bg-white/5 border-white/10'
            }`}
            data-testid={`tier-${t.id}`}
          >
            {t.highlight && (
              <span className="absolute -top-3 left-6 bg-teal-500 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full">
                Most Popular
              </span>
            )}
            <h3 className="text-white font-bold text-xl">{t.name}</h3>
            <p className="text-white/50 text-xs mt-1">{t.tagline}</p>
            <div className="mt-4 flex items-baseline gap-1">
              {t.price !== null ? (
                <>
                  <span className="text-white text-4xl font-black">${t.price}</span>
                  <span className="text-white/50 text-sm">/user/month</span>
                </>
              ) : (
                <span className="text-white text-3xl font-black">{t.priceLabel}</span>
              )}
            </div>
            {t.minMonthly && (
              <p className="text-white/40 text-[11px] mt-1">
                A${t.minMonthly}/mo minimum · billed annually
              </p>
            )}
            <ul className="mt-6 space-y-2.5">
              {t.features.map((f) => {
                const parts = f.split(/(\*\*[^*]+\*\*)/g);
                return (
                  <li key={f} className="flex items-start gap-2 text-sm text-white/80">
                    <CheckCircle2 className="w-4 h-4 text-teal-400 shrink-0 mt-0.5" />
                    <span>
                      {parts.map((p, i) => p.startsWith('**') && p.endsWith('**')
                        ? <strong key={i} className="text-white">{p.slice(2, -2)}</strong>
                        : <React.Fragment key={i}>{p}</React.Fragment>)}
                    </span>
                  </li>
                );
              })}
            </ul>
            <a
              href={t.id === 'enterprise' ? 'mailto:sales@yabbit.com.au?subject=VoiceFabric Enterprise enquiry' : '/login?trial=1'}
              className={`mt-6 w-full flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                t.highlight
                  ? 'bg-teal-500 hover:bg-teal-400 text-white'
                  : 'bg-white/10 hover:bg-white/20 text-white'
              }`}
              data-testid={`tier-cta-${t.id}`}
            >
              {t.id === 'enterprise' ? 'Talk to Sales' : 'Start Free Trial'}
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        ))}
      </div>
    </section>

    {/* Competitor comparison — the "why we're a bargain" table */}
    <section className="max-w-6xl mx-auto px-6 py-16 border-t border-white/5" data-testid="competitor-comparison">
      <div className="text-center mb-10">
        <p className="text-teal-300 text-xs font-semibold uppercase tracking-wider">The market</p>
        <h2 className="text-3xl sm:text-4xl font-black text-white mt-2">How VoiceFabric compares — Feb 2026</h2>
        <p className="text-white/50 text-sm mt-3 max-w-2xl mx-auto">
          Live pricing from vendor sites + analyst reports. Global competitors are US-priced enterprise contracts.
          Australian competitors are flat-monthly tiers that penalise growth.
        </p>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-white/10">
        <table className="w-full text-sm">
          <thead className="bg-white/5">
            <tr className="text-left text-white/60 text-xs uppercase tracking-wider">
              <th className="px-4 py-3">Platform</th>
              <th className="px-4 py-3">Positioning</th>
              <th className="px-4 py-3">Per-user cost (AUD)</th>
              <th className="px-4 py-3">Setup</th>
              <th className="px-4 py-3">Notes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            <tr className="bg-teal-500/10 text-white font-semibold" data-testid="competitor-row-voicefabric">
              <td className="px-4 py-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-teal-400" />Yabbit VoiceFabric
              </td>
              <td className="px-4 py-3 text-white/80 font-normal">AU · multi-role · per-user</td>
              <td className="px-4 py-3 text-teal-300">A$49 – A$149</td>
              <td className="px-4 py-3 text-white/80 font-normal">A$3.5K – A$11.9K</td>
              <td className="px-4 py-3 text-white/70 font-normal">5 roles · native Yabbit UCaaS · governance-independent</td>
            </tr>
            {COMPETITORS.map((c) => (
              <tr key={c.name} className="text-white/70">
                <td className="px-4 py-3 font-semibold text-white/90">{c.name}</td>
                <td className="px-4 py-3 text-xs">{c.vertical}</td>
                <td className="px-4 py-3 text-white/60">{c.price}</td>
                <td className="px-4 py-3 text-white/60">{c.setup}</td>
                <td className="px-4 py-3 text-xs">{c.notes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-white/30 text-[11px] mt-4 italic text-center">
        Sources: vendor public pricing pages · Vendr · CloudTalk · Balto.ai · Callyx.ai · WildJar · CallRail. AUD conversions at 1 USD ≈ 1.55 AUD.
      </p>
    </section>

    {/* ROI Calculator */}
    <section className="max-w-6xl mx-auto px-6 py-16">
      <RoiCalculator />
    </section>

    {/* Trust bar */}
    <section className="max-w-5xl mx-auto px-6 py-16 border-t border-white/5">
      <div className="grid md:grid-cols-3 gap-8 text-center">
        <div>
          <ShieldCheck className="w-8 h-8 text-teal-400 mx-auto mb-3" />
          <h3 className="text-white font-semibold">Australian data residency</h3>
          <p className="text-white/50 text-sm mt-1">Every tenant isolated · retention TTL configurable · Privacy Act 2026 ready.</p>
        </div>
        <div>
          <Zap className="w-8 h-8 text-teal-400 mx-auto mb-3" />
          <h3 className="text-white font-semibold">Live in 4 weeks</h3>
          <p className="text-white/50 text-sm mt-1">Onboarding call · KB workshop · baseline scoring by week 3.</p>
        </div>
        <div>
          <TrendingUp className="w-8 h-8 text-teal-400 mx-auto mb-3" />
          <h3 className="text-white font-semibold">15-30pt lift in 8 weeks</h3>
          <p className="text-white/50 text-sm mt-1">Real-customer average score gain across the first two coaching cycles.</p>
        </div>
      </div>
    </section>

    <footer className="border-t border-white/5 py-8 text-center text-white/30 text-xs">
      © 2026 Yabbit VoiceFabric · Powered by Yabbit UCaaS · yabbit.com.au
    </footer>
  </div>
);

export default PricingPage;
